import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useToast } from '../../../contexts/ToastContext';
import { useUploadPhotoToMaintenanceRequestMutation, useUpdateMaintenanceRequestMutation } from '../../../store/api/maintenanceApi';
import type { MaintenanceRequest } from '../../../types';

interface TaskDetailPanelProps {
	task: MaintenanceRequest | null;
	onTaskUpdate: (taskId: string, updates: any) => Promise<void>;
	onStatusUpdate: (taskId: string, newStatus: string, notes?: string) => void;
}

interface DisplayedPhoto {
	id: string;
	name: string;
	url: string;
	timestamp: string;
	isExisting: boolean;
}

const TaskDetailPanel: React.FC<TaskDetailPanelProps> = ({ task, onTaskUpdate, onStatusUpdate }) => {
	const navigate = useNavigate();
	const { success, error: showError } = useToast();
	const [timeSpent, setTimeSpent] = useState(task?.timeSpent || '');
	const [notes, setNotes] = useState('');
	const [uploadPhoto] = useUploadPhotoToMaintenanceRequestMutation();
	const [updateTask] = useUpdateMaintenanceRequestMutation();
	const [isUploading, setIsUploading] = useState(false);

	// Initialize displayed photos from task data
	const [displayedPhotos, setDisplayedPhotos] = useState<DisplayedPhoto[]>([]);

	useEffect(() => {
		if (task?.photo_urls) {
			setDisplayedPhotos(
				task.photo_urls.map((url: string, index: number): DisplayedPhoto => ({
					id: `existing-${index}`,
					name: `Photo ${index + 1}`,
					url: url,
					timestamp: new Date().toISOString(),
					isExisting: true,
				}))
			);
		}
	}, [task?.photo_urls]);

	if (!task) {
		return (
			<div className="bg-card border border-border rounded-lg p-6">
				<div className="text-center py-8">
					<div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
						<Icon name="FileText" size={24} className="text-muted-foreground" />
					</div>
					<p className="text-muted-foreground">Select a task to view details</p>
				</div>
			</div>
		);
	}

	const getPriorityColor = (priority: string): string => {
		switch (priority) {
			case 'high': return 'text-error border-error bg-error/10';
			case 'medium': return 'text-warning border-warning bg-warning/10';
			case 'low': return 'text-success border-success bg-success/10';
			default: return 'text-muted-foreground border-border bg-muted/10';
		}
	};

	const getStatusColor = (status: string): string => {
		switch (status) {
			case 'assigned': return 'text-warning bg-warning/10';
			case 'in-progress': return 'text-primary bg-primary/10';
			case 'completed': return 'text-success bg-success/10';
			case 'requires-follow-up': return 'text-error bg-error/10';
			default: return 'text-muted-foreground bg-muted/10';
		}
	};

	const formatStatus = (status: string): string => {
		return status.split('-').map((word: string) =>
			word.charAt(0).toUpperCase() + word.slice(1)
		).join(' ');
	};

	const convertFileToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
		});
	};

	const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
		const files = Array.from(event.target.files || []);
		if (files.length === 0) return;

		setIsUploading(true);

		for (const file of files) {
			try {
				// Validate file type - allow jpg, jpeg, png
				const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
				const validExtensions = ['.jpg', '.jpeg', '.png'];
				const isValidType = validTypes.includes(file.type?.toLowerCase());
				const isValidExtension = validExtensions.some(ext =>
					file.name?.toLowerCase().endsWith(ext)
				);
				if (!isValidType && !isValidExtension) {
					showError(`${file.name} is not a valid image type. Please upload JPG, JPEG, or PNG files.`);
					continue;
				}

				// Validate file size (max 5MB)
				if (file.size > 5 * 1024 * 1024) {
					showError(`${file.name} is too large. Maximum file size is 5MB.`);
					continue;
				}

				// Convert to base64
				const base64File = await convertFileToBase64(file);

				// Upload to backend
				const result = await uploadPhoto({
					id: task.id,
					data: { photo_file: base64File },
				}).unwrap();

				// Add to displayed photos
				const newPhotoUrl = result.photo_urls?.[result.photo_urls.length - 1];
				if (newPhotoUrl) {
					setDisplayedPhotos((prev: DisplayedPhoto[]): DisplayedPhoto[] => [
						...prev,
						{
							id: `uploaded-${Date.now()}`,
							name: file.name,
							url: newPhotoUrl,
							timestamp: new Date().toISOString(),
							isExisting: true,
						},
					]);
				}

				success(`Photo "${file.name}" uploaded successfully!`);
			} catch (err: any) {
				showError(
					err?.data?.detail ||
						`Failed to upload photo "${file.name}". Please try again.`
				);
			}
		}

		setIsUploading(false);
		// Clear the file input
		event.target.value = '';
	};

	const handleStatusChange = (newStatus: string): void => {
		onStatusUpdate(task.id, newStatus, notes);
		setNotes('');
	};

	const handleSaveProgress = async () => {
		try {
			await updateTask({
				id: task.id,
				data: {
					notes: notes || task.notes,
					time_spent: timeSpent ? parseFloat(timeSpent) : task.time_spent,
				},
			}).unwrap();
			success('Progress saved successfully!');
			setNotes('');
		} catch (err: any) {
			showError(
				err?.data?.detail ||
					'Failed to save progress. Please try again.'
			);
		}
	};

	return (
		<div className="bg-card border border-border rounded-lg">
			{/* Header */}
			<div className="p-6 border-b border-border">
				<div className="flex items-start justify-between mb-3">
					<div>
						<h3 className="text-lg font-semibold text-foreground">{task.title}</h3>
						<p className="text-sm text-muted-foreground mt-1">Task ID: {task.id}</p>
					</div>
					<div className="flex items-center space-x-2">
						<span className={`px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(task.priority)}`}>
							{task.priority} priority
						</span>
						<span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
							{formatStatus(task.status)}
						</span>
					</div>
				</div>
				<p className="text-sm text-muted-foreground">{task.description}</p>
			</div>

			{/* Task Details */}
			<div className="p-6 space-y-4">
				{/* Location & Schedule */}
				<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
					<div className="space-y-2">
						<div className="flex items-center space-x-2 text-sm">
							<Icon name="MapPin" size={16} className="text-muted-foreground" />
							<span className="text-foreground font-medium">Location</span>
						</div>
						<p className="text-sm text-muted-foreground ml-6">
							{task.property_name || (typeof task.property === 'string' ? task.property : task.property?.name || 'Property')}
							{task.property_address && <><br />{task.property_address}</>}
							{task.location && <><br />Location: {task.location}</>}
						</p>
					</div>
					<div className="space-y-2">
						<div className="flex items-center space-x-2 text-sm">
							<Icon name="Calendar" size={16} className="text-muted-foreground" />
							<span className="text-foreground font-medium">Scheduled</span>
						</div>
						<p className="text-sm text-muted-foreground ml-6">
							{task.scheduledDate
								? (typeof task.scheduledDate === 'string'
									? task.scheduledDate
									: task.scheduledDate.toLocaleDateString())
								: "Not scheduled"}
							{task.scheduledTime && <><br />{task.scheduledTime}</>}
							{task.estimatedTime && task.estimatedTime !== "N/A" && (
								<> ({task.estimatedTime})</>
							)}
						</p>
					</div>
				</div>

				{/* Contact Info */}
				{task.tenantContact?.name && (
					<div className="space-y-2">
						<div className="flex items-center space-x-2 text-sm">
							<Icon name="User" size={16} className="text-muted-foreground" />
							<span className="text-foreground font-medium">Tenant Contact</span>
						</div>
						<div className="ml-6 space-y-1">
							<p className="text-sm text-foreground">{task.tenantContact.name}</p>
							{(task.tenantContact.phone || task.tenantContact.email) && (
								<div className="flex items-center space-x-4 text-sm text-muted-foreground">
									{task.tenantContact.phone && (
										<a href={`tel:${task.tenantContact.phone}`} className="hover:text-primary">
											{task.tenantContact.phone}
										</a>
									)}
									{task.tenantContact.email && (
										<a href={`mailto:${task.tenantContact.email}`} className="hover:text-primary">
											{task.tenantContact.email}
										</a>
									)}
								</div>
							)}
						</div>
					</div>
				)}

				{/* Access Instructions / Notes */}
				{task.notes && (
					<div className="space-y-2">
						<div className="flex items-center space-x-2 text-sm">
							<Icon name="Key" size={16} className="text-muted-foreground" />
							<span className="text-foreground font-medium">Notes</span>
						</div>
						<p className="text-sm text-muted-foreground ml-6">{task.notes}</p>
					</div>
				)}

				{/* Progress Tracking */}
				{(task.status === 'in-progress' || task.status === 'completed') && (
					<div className="border-t border-border pt-4 space-y-4">
						<h4 className="font-medium text-foreground">Progress Tracking</h4>

						{/* Time Spent */}
						<div className="space-y-2">
							<label className="text-sm font-medium text-foreground">Time Spent</label>
							<input
								type="text"
								value={timeSpent}
								onChange={(e) => setTimeSpent(e.target.value)}
								placeholder="e.g., 2.5 hours"
								className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
							/>
						</div>

						{/* Notes */}
						<div className="space-y-2">
							<label className="text-sm font-medium text-foreground">Progress Notes</label>
							<textarea
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								placeholder="Add notes about progress, issues encountered, etc."
								rows={3}
								className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
							/>
						</div>

						{/* Photo Upload */}
						<div className="space-y-2">
							<label className="text-sm font-medium text-foreground">Photos</label>
							<div className="space-y-2">
								<input
									type="file"
									multiple
									accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
									onChange={handlePhotoUpload}
									className="hidden"
									id="photo-upload"
									disabled={isUploading}
								/>
								<label
									htmlFor="photo-upload"
									className={`inline-flex items-center space-x-2 px-3 py-2 text-sm border border-border rounded-md cursor-pointer hover:bg-muted transition-colors ${
										isUploading ? 'opacity-50 cursor-not-allowed' : ''
									}`}
								>
									<Icon name="Camera" size={16} />
									<span>{isUploading ? 'Uploading...' : 'Add Photos'}</span>
								</label>
								<p className="text-xs text-muted-foreground">
									Upload photos to document your work (max 5MB per photo)
								</p>

								{displayedPhotos.length > 0 && (
									<div className="grid grid-cols-2 gap-2 mt-3">
										{displayedPhotos.map((photo) => (
											<div key={photo.id} className="relative group">
												<img
													src={photo.url}
													alt={photo.name}
													className="w-full h-20 object-cover rounded border border-border"
												/>
												<div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 truncate">
													{photo.name}
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						</div>

						<Button
							variant="outline"
							onClick={handleSaveProgress}
							iconName="Save"
							iconPosition="left"
							className="w-full"
						>
							Save Progress
						</Button>
					</div>
				)}

				{/* Action Buttons */}
				<div className="border-t border-border pt-4 space-y-2">
					{task.status === 'assigned' && (
						<Button
							variant="default"
							onClick={() => handleStatusChange('in-progress')}
							iconName="Play"
							iconPosition="left"
							className="w-full"
						>
							Start Task
						</Button>
					)}

					{task.status === 'in-progress' && (
						<>
							<Button
								variant="default"
								onClick={() => handleStatusChange('completed')}
								iconName="Check"
								iconPosition="left"
								className="w-full"
							>
								Mark Complete
							</Button>
							<Button
								variant="outline"
								onClick={() => handleStatusChange('requires-follow-up')}
								iconName="AlertCircle"
								iconPosition="left"
								className="w-full"
							>
								Requires Follow-up
							</Button>
						</>
					)}

					{task.status === 'requires-follow-up' && (
						<Button
							variant="outline"
							onClick={() => handleStatusChange('assigned')}
							iconName="RefreshCw"
							iconPosition="left"
							className="w-full"
						>
							Reschedule Task
						</Button>
					)}

					<div className="flex space-x-2">
						<Button
							variant="outline"
							iconName="MessageSquare"
							iconPosition="left"
							className="flex-1"
							onClick={() => navigate("/messages")}
						>
							Contact Tenant
						</Button>
						<Button
							variant="outline"
							iconName="Phone"
							iconPosition="left"
							className="flex-1"
						>
							Call Manager
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default TaskDetailPanel;
